import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  ProjectsResponseSchema,
  PullRequestProjectSchema,
  PullRequestProjectStatusSchema,
  type ProjectsResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/projects'

import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {pullRequestsOverviewRoute} from '../routes/overview-route'

export type MutationData = {
  projectId: string
  itemId: string
  fieldId: string
  optionId: string
}

type Callbacks = {
  onError?: (error: Error) => void
}

export function useUpdateProjectStatus({onError}: Callbacks = {}) {
  const projectsQueryKey = useRouteQueryKey(pullRequestsOverviewRoute, 'projects')
  const updateProjectStatusUrl = usePageDataUrl(PageData.updateProjectStatus)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(updateProjectStatusUrl, {
        method: 'PUT',
        headers: {Accept: 'application/json'},
        body: {itemId: data.itemId, fieldId: data.fieldId, optionId: data.optionId},
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },
    onMutate: async data => {
      if (!projectsQueryKey) return

      await queryClient.cancelQueries({queryKey: projectsQueryKey})
      const previous = queryClient.getQueryData<ProjectsResponse>(projectsQueryKey)

      if (previous) {
        queryClient.setQueryData<ProjectsResponse>(projectsQueryKey, () =>
          create(ProjectsResponseSchema, {
            projects: previous.projects.map(project => {
              if (project.id !== data.projectId || !project.status) return project

              return create(PullRequestProjectSchema, {
                id: project.id,
                itemId: project.itemId,
                title: project.title,
                number: project.number,
                url: project.url,
                closed: project.closed,
                viewerCanUpdate: project.viewerCanUpdate,
                status: create(PullRequestProjectStatusSchema, {
                  fieldId: project.status.fieldId,
                  optionId: data.optionId,
                  options: project.status.options,
                }),
              })
            }),
          }),
        )
      }

      return {previous}
    },
    onError: (e: Error, _vars, context) => {
      if (projectsQueryKey && context?.previous) {
        queryClient.setQueryData(projectsQueryKey, context.previous)
      }
      onError?.(e)
    },
    onSettled: () => {
      if (!projectsQueryKey) return
      return queryClient.invalidateQueries({queryKey: projectsQueryKey})
    },
  })
}
